package fyi.moko.hotpl.data.image

import fyi.moko.hotpl.ui.placeform.JPEG_QUALITY
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import kotlin.js.Promise
import kotlin.math.PI

/**
 * JS/web [ImageRotator]: `createImageBitmap` on a JPEG blob, redrawn onto a canvas whose
 * width and height are swapped, re-encoded with `toBlob`.
 *
 * 픽커([fyi.moko.hotpl.ui.placeform.rememberMultiImagePicker])는 `<img>` 폴백까지 두지만
 * 여기는 두지 않는다 — 우리가 이미 JPEG으로 구워 저장한 바이트라, 브라우저가 HEIC로
 * 애먹을 일이 없다.
 */
class JsImageRotator : ImageRotator {

    override suspend fun rotate90(bytes: ByteArray): ByteArray? {
        if (bytes.isEmpty()) return null
        return runCatching {
            val blob = Blob(arrayOf(bytes.toUint8Array()), BlobPropertyBag(type = "image/jpeg"))
            val bitmap = window.createImageBitmap(blob).await()
            val canvas = document.createElement("canvas") as HTMLCanvasElement
            canvas.width = bitmap.height
            canvas.height = bitmap.width
            val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
            ctx.translate(bitmap.height / 2.0, bitmap.width / 2.0)
            ctx.rotate(PI / 2.0)
            ctx.drawImage(bitmap, -bitmap.width / 2.0, -bitmap.height / 2.0)
            bitmap.close()

            val out: Blob = Promise<Blob> { resolve, _ ->
                canvas.asDynamic().toBlob({ b: Blob -> resolve(b) }, "image/jpeg", JPEG_QUALITY / 100.0)
            }.await()
            out.toByteArray()
        }.onFailure { console.error("JsImageRotator: rotate failed", it) }.getOrNull()
    }
}

private fun ByteArray.toUint8Array(): Uint8Array {
    val array = Uint8Array(size)
    forEachIndexed { i, byte -> array.asDynamic()[i] = byte }
    return array
}

private suspend fun Blob.toByteArray(): ByteArray {
    val buffer: ArrayBuffer = asDynamic().arrayBuffer().unsafeCast<Promise<ArrayBuffer>>().await()
    val uint8 = Uint8Array(buffer)
    return ByteArray(uint8.length) { i -> uint8.asDynamic()[i] as Byte }
}
